// TreeWalker for text nodes plus attribute scan plus a MutationObserver
// to catch dynamic content. Skips <script>, <style>, code blocks.

const ATTRS = ["title", "alt", "placeholder", "aria-label", "aria-description", "data-original-title"];
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "PRE"]);

function compile(rules) {
	const compiled = [];
	rules.forEach((r) => {
		if (!r.find) return;
		try {
			let src;
			const flags = r.case_sensitive ? "g" : "gi";
			if (r.is_regex) {
				src = r.find;
			} else {
				src = r.find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
				if (r.whole_word) src = `\\b${src}\\b`;
			}
			compiled.push({ re: new RegExp(src, flags), replace: r.replace || "" });
		} catch (e) {
			// invalid pattern - skip
		}
	});
	return compiled;
}

function applyToString(s, rules) {
	if (!s) return s;
	let out = s;
	for (const { re, replace } of rules) out = out.replace(re, replace);
	return out;
}

function walkNode(root, rules, opts) {
	if (opts.scan_text_nodes !== false) {
		const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
			acceptNode: (n) => {
				if (!n.nodeValue || !n.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
				if (n.parentElement && SKIP_TAGS.has(n.parentElement.tagName)) return NodeFilter.FILTER_REJECT;
				return NodeFilter.FILTER_ACCEPT;
			},
		});
		let n;
		while ((n = walker.nextNode())) {
			const nv = applyToString(n.nodeValue, rules);
			if (nv !== n.nodeValue) n.nodeValue = nv;
		}
	}
	if (opts.scan_attributes !== false) {
		const els = root.nodeType === 1 ? [root, ...root.querySelectorAll("*")] : root.querySelectorAll("*");
		els.forEach((el) => {
			ATTRS.forEach((a) => {
				const v = el.getAttribute && el.getAttribute(a);
				if (!v) return;
				const nv = applyToString(v, rules);
				if (nv !== v) el.setAttribute(a, nv);
			});
		});
	}
}

function updateTitleAndMeta(rules) {
	if (document.title) {
		const t = applyToString(document.title, rules);
		if (t !== document.title) document.title = t;
	}
	document.querySelectorAll("meta[name='description'], meta[name='application-name'], meta[property^='og:']").forEach((m) => {
		const v = m.getAttribute("content");
		if (!v) return;
		const nv = applyToString(v, rules);
		if (nv !== v) m.setAttribute("content", nv);
	});
}

export function installTextReplacer(rules, opts = {}) {
	if (!rules || !rules.length) return () => {};
	const compiled = compile(rules);
	if (!compiled.length) return () => {};

	const run = (root) => walkNode(root || document.body, compiled, opts);
	run(document.body);
	if (opts.scan_titles_and_meta !== false) updateTitleAndMeta(compiled);

	let pending = false;
	const observer = new MutationObserver((mutations) => {
		if (pending) return;
		pending = true;
		requestAnimationFrame(() => {
			pending = false;
			const touched = new Set();
			for (const m of mutations) {
				if (m.type === "childList") {
					m.addedNodes.forEach((n) => {
						if (n.nodeType === 1 && !touched.has(n)) {
							touched.add(n);
							walkNode(n, compiled, opts);
						} else if (n.nodeType === 3 && n.nodeValue) {
							const nv = applyToString(n.nodeValue, compiled);
							if (nv !== n.nodeValue) n.nodeValue = nv;
						}
					});
				} else if (m.type === "characterData" && m.target.nodeValue) {
					const nv = applyToString(m.target.nodeValue, compiled);
					if (nv !== m.target.nodeValue) m.target.nodeValue = nv;
				} else if (m.type === "attributes" && ATTRS.includes(m.attributeName)) {
					const el = m.target;
					const v = el.getAttribute(m.attributeName);
					if (v) {
						const nv = applyToString(v, compiled);
						if (nv !== v) el.setAttribute(m.attributeName, nv);
					}
				}
			}
			if (opts.scan_titles_and_meta !== false) updateTitleAndMeta(compiled);
		});
	});
	observer.observe(document.body, {
		subtree: true,
		childList: true,
		characterData: true,
		attributes: true,
		attributeFilter: ATTRS,
	});

	return () => observer.disconnect();
}
